import { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import {
  acknowledgementSchema,
  authSessionSchema,
  privateUserSchema,
  registerResultSchema,
} from '@/lib/schemas/user';

export interface LoginInput {
  email: string;
  password: string;
}

export interface RegisterBusinessInput {
  name: string;
  legalName?: string;
  category: string;
  town: string;
  countryCode: string;
  addressLine1?: string;
  postalCode?: string;
  websiteUrl?: string;
  phone?: string;
  registrationId?: string;
}

export interface RegisterInput {
  /** The plan a business is signing up on. A paid one is charged first. */
  tier?: 'FREE' | 'LOCAL' | 'MULTI';
  /** Omitted for a personal account, which is what the API assumes. */
  accountType?: 'PERSONAL' | 'BUSINESS';
  /** Required with `accountType: 'BUSINESS'`, and rejected without it. */
  business?: RegisterBusinessInput;
  email: string;
  password: string;
  displayName: string;
  addressLine1: string;
  addressLine2?: string;
  town: string;
  region?: string;
  postalCode?: string;
  countryCode: string;
}

export const authApi = {
  me: (signal?: AbortSignal) =>
    apiClient.get('/auth/me', privateUserSchema, signal ? { signal } : {}),

  login: (body: LoginInput) => apiClient.post('/auth/login', authSessionSchema, { body }),

  register: (body: RegisterInput) =>
    apiClient.post('/auth/register', registerResultSchema, { body }),

  /** Finishes a signup that was waiting on a payment, and signs them in. */
  completeRegistration: (sessionId: string) =>
    apiClient.post('/auth/complete-registration', authSessionSchema, { body: { sessionId } }),

  logout: () => apiClient.post('/auth/logout', z.null()),

  logoutAll: () => apiClient.post('/auth/logout-all', z.null()),

  verifyEmail: (token: string) =>
    apiClient.post('/auth/verify-email', privateUserSchema, { body: { token } }),

  resendVerification: (email: string) =>
    apiClient.post('/auth/resend-verification', acknowledgementSchema, { body: { email } }),

  forgotPassword: (email: string) =>
    apiClient.post('/auth/forgot-password', acknowledgementSchema, { body: { email } }),

  resetPassword: (token: string, password: string) =>
    apiClient.post('/auth/reset-password', acknowledgementSchema, {
      body: { token, password },
    }),
};
